import { useState } from 'react'
import type { ChatEntry } from '@/hooks/useChat'
import { RenderCanvas } from '@/components/render/RenderCanvas'
import type { CustomerConfirmCandidate, RenderPart, TextPart } from '@/lib/render'
import { isSettleHandoff, settleHandoffSummary } from '@/lib/settleHandoff'
import { stripMentionContext } from '@/lib/mentions'
import { stripScreenContext } from '@/lib/pageContext'
import { useBranding } from '@/contexts/BrandingContext'
import { cn } from '@/lib/utils'

interface MessageBubbleProps {
  entry: ChatEntry
  /** Operator surfaces only — enables quick actions on rendered calendar tiles. */
  interactive?: boolean
  /** Sends a button-driven renderer's choice into the chat as the user's next message. */
  onQuickReply?: (text: string) => void
  /** Operator surfaces only — reports a customer picked in a renderer so the composer pin can follow. */
  onCustomerPick?: (customer: CustomerConfirmCandidate) => void
}

// The calendar's Pay handoff is machine-shaped on purpose — booking, customer
// and package ids must live in the TEXT so the model can act on them turns
// later. The operator shouldn't have to read UUIDs, so this ONE message type
// renders collapsed to its human summary with the full text behind a toggle.
// Display only: the entry content (and what the model receives) is unchanged.
function SettleHandoffBubble({ content }: { content: string }) {
  const branding = useBranding()
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="mb-1 flex justify-end">
      <div
        className="flex min-w-0 max-w-[80%] flex-col rounded-[18px] rounded-br-[4px] px-3.5 py-2.5"
        style={{ backgroundColor: branding.primaryColor }}
      >
        <span className="mb-0.5 text-[11px] font-semibold uppercase tracking-[0.5px] text-white/70">
          💳 Payment request from the calendar
        </span>
        <p className="text-[15px] leading-[21px] break-words whitespace-pre-wrap text-white">
          {expanded ? content : settleHandoffSummary(content)}
        </p>
        <button
          type="button"
          className="mt-1 self-start text-[12px] font-medium text-white/75 underline"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'Hide details' : 'Show details'}
        </button>
      </div>
    </div>
  )
}

export function MessageBubble({ entry, interactive, onQuickReply, onCustomerPick }: MessageBubbleProps) {
  const branding = useBranding()

  if (entry.role === 'user') {
    if (entry.content && isSettleHandoff(entry.content)) {
      return <SettleHandoffBubble content={entry.content} />
    }
    return (
      <div className="mb-1 flex justify-end">
        <div
          className="flex min-w-0 max-w-[80%] flex-col rounded-[18px] rounded-br-[4px] px-3.5 py-2.5"
          style={{ backgroundColor: branding.primaryColor }}
        >
          {entry.attachedFileNames?.map((name, i) => (
            <span key={`${name}-${i}`} className="mb-1 truncate text-[12px] text-white/75">📎 {name}</span>
          ))}
          {/* Marker lines carry ids in the text so the model can act on them
              turns later; the operator only ever reads names. Mentions strip
              first (the mentions marker is terminal), then screen context —
              same order as the panel's UserMessage. */}
          <p className="text-[15px] leading-[21px] break-words whitespace-pre-wrap text-white">
            {stripScreenContext(stripMentionContext(entry.content ?? ''))}
          </p>
        </div>
      </div>
    )
  }

  // System events (handoff, hand-back) render as a centered subtle line.
  if (entry.senderType === 'system') {
    const text = (entry.parts ?? []).map(p => (p.kind === 'text' ? (p as TextPart).content : '')).join(' ').trim()
    if (!text) return null
    return (
      <div className="my-1.5 flex flex-col items-center">
        <span className="rounded-lg bg-muted px-2.5 py-1 text-[12px] text-muted-foreground italic">{text}</span>
      </div>
    )
  }

  // Assistant message: render each part in order
  const parts = entry.parts ?? []

  if (parts.length === 0) return null

  // A named human team member gets a distinct accent; innoAI uses the default.
  const isHuman = entry.senderType === 'staff'

  return (
    <div className="mb-1 flex justify-start">
      <div className="flex min-w-0 max-w-[96%] flex-1 flex-col gap-1.5">
        {entry.senderLabel && (
          <span
            className={cn(
              'mb-px ml-1 text-[11px] font-semibold text-muted-foreground',
              isHuman && 'text-teal-600',
            )}
          >
            {isHuman ? '👤 ' : ''}{entry.senderLabel}
          </span>
        )}
        {parts.map((part, i) => {
          if (part.kind === 'text') {
            const textPart = part as TextPart
            return (
              <div
                key={i}
                className={cn(
                  'self-start rounded-[18px] rounded-bl-[4px] border border-border bg-card px-3.5 py-2.5 shadow-sm',
                  isHuman && 'border-teal-200 bg-teal-50',
                )}
              >
                <p className="text-[15px] leading-[22px] break-words whitespace-pre-wrap text-foreground">
                  {textPart.content}
                </p>
              </div>
            )
          }

          if (part.kind === 'render') {
            // render components fill the available width
            return (
              <div key={i} className="min-w-0 self-stretch">
                <RenderCanvas
                  part={part as RenderPart}
                  interactive={interactive}
                  onQuickReply={onQuickReply}
                  onCustomerPick={onCustomerPick}
                />
              </div>
            )
          }

          return null
        })}

        {entry.error ? (
          <span className="mt-0.5 text-[12px] text-orange-500">⚠ There was an issue processing this request.</span>
        ) : null}

        <span className="mt-0.5 px-1 text-[11px] text-muted-foreground">
          {entry.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  )
}
